import { pageTypes, type PageCodes } from '@dvag/dfs-constant-config';
import { type FavoritePage } from '@dvag/dfs-ui-blocks/components';
import { usePagesConfig } from '@dvag/dfs-ui-blocks/hooks';
import i18next from 'i18next';
import { useCallback, useMemo } from 'react';

import { Type, useGetPagesConfigurationQuery } from 'graphql/configuration/generates';
import { getGraphQLClient } from 'graphql/graphQLClient';
import featureFlag from 'service/featureFlag';

import { useGetPolicies } from './useGetPolicies';

export const useGetFavoritesCatalog = (nSimulationFlag: boolean) => {
  const query = useGetPagesConfigurationQuery(getGraphQLClient('configuration'), {
    type: Type.Favorite,
  });
  const policiesQuery = useGetPolicies();
  const policies = policiesQuery.data?.policies;
  const pagesConfigOptions = useMemo(
    () => ({
      isFeatureOn: (feature: string) => !!featureFlag.gb?.isOn(feature),
      policies: policies ?? [],
    }),
    [policies],
  );
  const configuredPages = usePagesConfig(
    query.data?.config.pages,
    pageTypes.FAVORITE,
    pagesConfigOptions,
  );

  const getPageName = useCallback(
    (translationKey: string) => {
      if (translationKey === 'SIMULATION' && nSimulationFlag) {
        return i18next.t(`pages.FUTURE_HOUSEHOLD_BUDGET`, { ns: 'pages' });
      }
      return i18next.t(`pages.${translationKey}`, translationKey, { ns: 'pages' });
    },
    [nSimulationFlag],
  );

  const catalog = useMemo<FavoritePage[] | undefined>(() => {
    if (!query.data?.config.pages) {
      return undefined;
    }

    return configuredPages.map((page) => ({
      permissions: page.permissions,
      ...(page.policies ? { policies: page.policies } : {}),
      code: page.code as PageCodes,
      navigationCode: page.navigationCode,
      name: getPageName(page.translationKey),
      aliases: page.aliases,
      featureFlags: page.featureFlags,
    }));
  }, [configuredPages, getPageName, query.data?.config.pages]);

  return {
    ...query,
    data: catalog,
    isLoading: query.isLoading || policiesQuery.isLoading,
  };
};
